import { useState, useEffect, useRef, useCallback } from "react";
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";

export interface ParsedAddress {
  street: string;
  city: string;
  province: string;
  country: string; // ISO 3166-1 alpha-2 code
  postalCode: string;
}

export interface PlaceSuggestion {
  placeId: string;
  description: string;
}

const API_KEY = import.meta.env.VITE_GOOGLE_PLACES_API_KEY as string | undefined;

export function useGooglePlacesAutocomplete() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const autocompleteService =
    useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);
  const sessionToken =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!API_KEY) return;

    setOptions({ key: API_KEY });

    importLibrary("places").then(() => {
      autocompleteService.current =
        new google.maps.places.AutocompleteService();
      const div = document.createElement("div");
      placesService.current = new google.maps.places.PlacesService(div);
      sessionToken.current =
        new google.maps.places.AutocompleteSessionToken();
      setIsLoaded(true);
    }).catch(() => {
      // Graceful degradation — autocomplete simply won't appear
    });
  }, []);

  const search = useCallback(
    (input: string) => {
      if (!autocompleteService.current) return;

      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      if (!input.trim()) {
        setSuggestions([]);
        return;
      }

      debounceTimer.current = setTimeout(() => {
        autocompleteService.current!.getPlacePredictions(
          {
            input,
            types: ["address"],
            sessionToken: sessionToken.current!,
          },
          (predictions, status) => {
            if (
              status === google.maps.places.PlacesServiceStatus.OK &&
              predictions
            ) {
              setSuggestions(
                predictions.map((p) => ({
                  placeId: p.place_id,
                  description: p.description,
                }))
              );
            } else {
              setSuggestions([]);
            }
          }
        );
      }, 300);
    },
    []
  );

  const selectPlace = useCallback(
    (placeId: string): Promise<ParsedAddress | null> => {
      return new Promise((resolve) => {
        if (!placesService.current) {
          resolve(null);
          return;
        }

        placesService.current.getDetails(
          {
            placeId,
            fields: ["address_components"],
            sessionToken: sessionToken.current!,
          },
          (place, status) => {
            // Refresh session token after a getDetails call (billing boundary)
            sessionToken.current =
              new google.maps.places.AutocompleteSessionToken();

            if (
              status !== google.maps.places.PlacesServiceStatus.OK ||
              !place?.address_components
            ) {
              resolve(null);
              return;
            }

            const get = (type: string): string => {
              const comp = place.address_components!.find((c) =>
                c.types.includes(type)
              );
              return comp?.long_name || "";
            };

            const getShort = (type: string): string => {
              const comp = place.address_components!.find((c) =>
                c.types.includes(type)
              );
              return comp?.short_name || "";
            };

            const streetNumber = get("street_number");
            const route = get("route");

            resolve({
              street: [streetNumber, route].filter(Boolean).join(" "),
              city: get("locality") || get("sublocality_level_1"),
              province: get("administrative_area_level_1"),
              country: getShort("country"), // ISO alpha-2
              postalCode: get("postal_code"),
            });
          }
        );
      });
    },
    []
  );

  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
  }, []);

  return { suggestions, search, selectPlace, clearSuggestions, isLoaded };
}
